/**
 * Solicitante da entrega, usado só para preencher endereços sob demanda.
 *
 * O GET da entrega pode devolver o solicitante em formatos diferentes (ou omitir o
 * addressId). Guardamos o que já conhecemos por entrega para o botão continuar
 * disponível depois de sair e voltar para a tela.
 */

const KEY_PREFIX = 'delivery-requester:';

export function normalizeDeliveryId(deliveryId) {
    if (deliveryId == null) return '';
    const raw = String(deliveryId).trim();
    if (!raw) return '';
    const last = raw.split('?')[0].replace(/\/+$/, '').split('/').pop();
    return last || raw;
}

function storageKey(deliveryId) {
    return `${KEY_PREFIX}${normalizeDeliveryId(deliveryId)}`;
}

export function numericIdOrNull(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'object') {
        return numericIdOrNull(raw.id ?? raw.href ?? raw.self);
    }
    const cleaned = String(raw).trim().replace(/[^\d-]/g, '');
    if (!cleaned || cleaned === '-') return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}

function idFromHref(href) {
    if (href == null || href === '') return null;
    const last = String(href).split('?')[0].replace(/\/+$/, '').split('/').pop();
    return numericIdOrNull(last);
}

/**
 * Extrai id da pessoa e id do endereço a partir de um objeto de pessoa/solicitante.
 * @param {unknown} source
 * @returns {{ id: number|null, addressId: number|null }}
 */
export function requesterFromPerson(source) {
    if (source == null || source === '') return { id: null, addressId: null };
    if (typeof source === 'number' || typeof source === 'string') {
        return { id: numericIdOrNull(source), addressId: null };
    }
    if (typeof source !== 'object') return { id: null, addressId: null };

    const id = numericIdOrNull(
        source.id
        ?? source.personId
        ?? source.employeeId
        ?? source.funcionarioId
        ?? source.requesterId
    ) ?? idFromHref(source._links?.self?.href ?? source.href);

    const address = source.address && typeof source.address === 'object' ? source.address : null;
    const addressId = numericIdOrNull(
        source.addressId
        ?? source.enderecoId
        ?? source.requesterAddressId
        ?? address?.id
        ?? address?.addressId
    );

    return { id, addressId };
}

/**
 * Solicitante do GET da entrega. Aceita requester, solicitante, responsible e ids planos.
 * @param {Object|null|undefined} dto
 * @returns {{ id: number|null, addressId: number|null }}
 */
export function requesterInfoFromDto(dto) {
    if (!dto || typeof dto !== 'object') return { id: null, addressId: null };

    const candidates = [
        dto.requester,
        dto.solicitante,
        dto.responsible,
        dto._embedded?.requester,
        dto._embedded?.solicitante,
        dto._embedded?.responsible,
        dto.requesterId,
        dto.solicitanteId,
    ];

    for (const candidate of candidates) {
        const parsed = requesterFromPerson(candidate);
        if (parsed.id != null || parsed.addressId != null) return parsed;
    }

    return {
        id: numericIdOrNull(dto.requesterId ?? dto.solicitanteId),
        addressId: numericIdOrNull(dto.requesterAddressId ?? dto.solicitanteAddressId),
    };
}

function hasRequester(requester) {
    return requester != null && (requester.id != null || requester.addressId != null);
}

/**
 * @param {string|number|null|undefined} deliveryId
 * @param {{ id?: number|string|null, addressId?: number|string|null }|null|undefined} requester
 */
export function saveDeliveryRequester(deliveryId, requester) {
    const id = normalizeDeliveryId(deliveryId);
    if (!id || !hasRequester(requester)) return;
    try {
        sessionStorage.setItem(storageKey(id), JSON.stringify({
            id: numericIdOrNull(requester.id),
            addressId: numericIdOrNull(requester.addressId),
        }));
    } catch {
        // Preenchimento pelo solicitante é um atalho opcional: falha de storage não afeta a tela.
    }
}

/**
 * @param {string|number|null|undefined} deliveryId
 * @returns {{ id: number|null, addressId: number|null }|null}
 */
export function readDeliveryRequester(deliveryId) {
    const id = normalizeDeliveryId(deliveryId);
    if (!id) return null;
    try {
        const raw = sessionStorage.getItem(storageKey(id));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        const requester = {
            id: numericIdOrNull(parsed.id),
            addressId: numericIdOrNull(parsed.addressId),
        };
        return hasRequester(requester) ? requester : null;
    } catch {
        return null;
    }
}
